import { addMonths, expirySortValue, now } from "../../shared/utils/date";
import { TExperience } from "./types";

export { formatDateYMD as formatDate } from "../../shared/utils/date";

export const nameAsc = (a: TExperience, b: TExperience): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

export const nameDesc = (a: TExperience, b: TExperience): number =>
  b.name.localeCompare(a.name, undefined, { sensitivity: "base" });

export const expiryAsc = (a: TExperience, b: TExperience): number =>
  expirySortValue(a.expiresAt) - expirySortValue(b.expiresAt);

export const expiryDesc = (a: TExperience, b: TExperience): number =>
  expirySortValue(b.expiresAt) - expirySortValue(a.expiresAt);

export const ymd = (d?: Date | null): string =>
  d ? d.toISOString().slice(0, 10) : "—";

export const computeNextRenewalDate = (licenseStart?: Date | null): Date => {
  // If there is a contract start, prefer it. Otherwise, 12 months from today.
  const base = licenseStart ?? now();

  return addMonths(base, 12);
};

export const blocksTooltip = (
  showRenewalDate: boolean,
  t: (key: string) => string,
): string =>
  showRenewalDate
    ? t("experiences.overviewTab.blockedSeatsTooltipWithRenewal")
    : t("experiences.overviewTab.blockedSeatsTooltip");

export function getContentMetadataId(x: unknown): string | undefined {
  if (!x || typeof x !== "object") return undefined;
  const obj = x as Record<string, unknown>;
  const v1 = obj["contentMetadataId"];
  if (typeof v1 === "string") return v1;
  const v2 = obj["contentMetadataID"];
  if (typeof v2 === "string") return v2;
  const rc = obj["releaseChannel"];
  if (rc && typeof rc === "object") {
    const v3 = (rc as Record<string, unknown>)["contentMetadataId"];
    if (typeof v3 === "string") return v3;
  }

  return undefined;
}

export async function runWithConcurrency<T, R>(
  items: readonly T[],
  fn: (item: T, index: number) => Promise<R>,
  concurrency = 5,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  const workerCount = Math.max(1, Math.min(concurrency, items.length));

  let index = 0;
  const getNext = () => {
    const i = index;
    index += 1;

    return i;
  };

  const workers = Array.from({ length: workerCount }, async () => {
    for (;;) {
      const i = getNext();
      if (i >= items.length) return;
      out[i] = await fn(items[i] as T, i);
    }
  });

  await Promise.all(workers);

  return out;
}
